import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orders as ordersApi, coins as coinsApi, compliance } from '../lib/api';
import { API_BASE } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CheckCircle2, Circle, Clock, ShieldCheck, Coins, Sparkles, Video, MessageCircle, FileDown, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import WhatsAppShare from '../components/WhatsAppShare';
import { PrintShell, PrintButton } from '../components/PrintShell';

const flow = ['placed', 'accepted', 'packed', 'out_for_delivery', 'delivered'];
const labels = {
  placed: 'Order placed',
  accepted: 'Accepted by store',
  packed: 'Packed & ready',
  preparing: 'Packed & ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
};

export default function OrderDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [retOpen, setRetOpen] = useState(false);
  const [retReason, setRetReason] = useState('');
  const [retVideo, setRetVideo] = useState('');
  const [scratch, setScratch] = useState(null);

  const load = () => ordersApi.get(id).then(setOrder).catch(() => {});
  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/'); return; }
    load(); /* eslint-disable-next-line */
  }, [id, user, loading, nav]);

  if (loading || !user) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;
  if (!order) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">Loading order…</div>;

  const isOwner = user && order.owner_id === user.id;
  const isCustomer = user && order.user_id === user.id;
  const isMedical = !!order.is_medical_order || !!order.prescription_url || (order.store?.category === 'medical');
  const evidenceUrl = order.evidence_pdf_url;

  const generateEvidence = async () => {
    setBusy(true);
    try {
      const r = await compliance.generateEvidencePdf(order.id);
      toast.success(r.already_generated ? 'Evidence PDF ready' : 'Evidence PDF generated');
      const abs = /^https?:/.test(r.url) ? r.url : `${API_BASE.replace(/\/api\/?$/, '')}${r.url}`;
      window.open(abs, '_blank', 'noopener,noreferrer');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate evidence PDF');
    } finally { setBusy(false); }
  };
  // Treat legacy "preparing" as "packed" for the new timeline
  const normalizedStatus = order.status === 'preparing' ? 'packed' : order.status;
  const currentIdx = flow.indexOf(normalizedStatus);

  const advance = async (next) => {
    setBusy(true);
    try {
      // If moving to delivered and OTP required, attach OTP from owner's input
      const payload = (next === 'delivered' && order.otp_required && !order.otp_verified) ? otpInput : undefined;
      const o = await ordersApi.updateStatus(order.id, next, payload);
      setOrder(o);
      toast.success(`Status updated to ${labels[next] || next}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (!otpInput || otpInput.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setBusy(true);
    try {
      await ordersApi.verifyOtp(order.id, otpInput);
      await load();
      toast.success('OTP verified — you can now mark delivered.');
      setOtpInput('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Incorrect OTP');
    } finally { setBusy(false); }
  };

  const submitReturn = async () => {
    if (!retReason.trim()) { toast.error('Tell the seller why you want to return'); return; }
    if (order.video_return_required && !retVideo.trim()) { toast.error('Video link is required for this order'); return; }
    setBusy(true);
    try {
      await ordersApi.returnRequest(order.id, { reason: retReason, video_url: retVideo });
      await load();
      toast.success('Return request submitted');
      setRetOpen(false); setRetReason(''); setRetVideo('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit return');
    } finally { setBusy(false); }
  };

  const revealScratch = async () => {
    try {
      const r = await coinsApi.scratch(order.id);
      setScratch(r);
      toast.success(`You won ${r.coins} coins!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cannot reveal yet');
    }
  };

  const otpWhatsappUrl = order.otp_required && order.delivery_otp
    ? `https://wa.me/?text=${encodeURIComponent(`BharatCart order #${order.id.slice(0,8)}\nDelivery OTP: ${order.delivery_otp}\n\nShare with the delivery person only when you receive your goods.`)}`
    : null;

  return (
    <div className="mandispot-printable max-w-3xl mx-auto px-4 md:px-6 py-10">
      <PrintShell
        kind="Order Bill / Invoice"
        docId={`#${order.id.slice(0, 8).toUpperCase()}`}
        dateText={new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
      />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Order #{order.id.slice(0,8)}</div>
          <h1 data-testid="order-store-name" className="font-heading font-black text-3xl mt-1">{order.store_name}</h1>
          <div className="text-sm text-ink/60">Placed {new Date(order.created_at).toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/50">Total</div>
          <div className="font-heading font-black text-3xl">₹{order.total.toFixed(2)}</div>
          <div className="text-xs text-ink/60 capitalize">{order.payment_method} · {order.payment_status}</div>
          <WhatsAppShare
            testId="share-order-whatsapp"
            className="mt-3"
            label="Share on WhatsApp"
            text={`📦 *BharatCart order* — India's Marketplace — Brands, Makers & Local Shops\n\nOrder #${order.id.slice(0,8)} from *${order.store_name}*\nTotal: ₹${order.total.toFixed(2)}\n\nTrack live:\n${window.location.href}`}
          />
          {order.payment_method === 'whatsapp' && (
            <button
              data-testid="reopen-whatsapp-order-btn"
              onClick={async () => {
                try {
                  const { data } = await import('../lib/api').then(m => m.api.get(`/orders/${order.id}/whatsapp-link`));
                  window.open(data.wa_url, '_blank', 'noopener');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't open WhatsApp");
                }
              }}
              className="mt-2 w-full inline-flex justify-center items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-4 py-2 text-sm font-bold transition"
            >
              <MessageCircle className="w-4 h-4"/>Re-send order to owner on WhatsApp
            </button>
          )}          {localStorage.getItem('mandispot_token') && (
            <WhatsAppShare
              testId="share-bill-pdf-whatsapp"
              className="mt-2 !bg-[#1B5E20] hover:!bg-[#0E3F11]"
              label="Send PDF bill on WhatsApp"
              text={`🧾 *BharatCart — Your PDF Bill*\n\nOrder #${order.id.slice(0,8)} · ${order.store_name}\nTotal: ₹${order.total.toFixed(2)}\n\nDownload the bill (with barcode for every item):\n${API_BASE}/orders/${order.id}/bill.pdf?token=${localStorage.getItem('mandispot_token')}\n\nMade for Bharat — सबका bazaar, ek tap par.`}
            />
          )}
          <div className="mt-2 flex justify-end gap-2 flex-wrap">
            <a
              data-testid="track-shipment-btn"
              href={`/track/${order.id}`}
              className="no-print inline-flex items-center gap-1.5 rounded-full border border-[#1B5E20] bg-[#1B5E20] text-white hover:bg-[#0D3D14] hover:border-[#0D3D14] px-3 py-1.5 text-xs font-semibold transition"
            >
              📍 Track shipment
            </a>
            <PrintButton label="Print bill" testId="print-order-btn"/>
            {isMedical && (isOwner || isCustomer) && (
              <button
                data-testid="evidence-pdf-btn"
                onClick={generateEvidence}
                disabled={busy}
                className="no-print inline-flex items-center gap-1.5 rounded-full border border-[#B71C1C] bg-white text-[#B71C1C] hover:bg-[#FFEBEE] px-3 py-1.5 text-xs font-black uppercase tracking-wider transition disabled:opacity-60"
              >
                <ScrollText className="w-3.5 h-3.5"/>
                {evidenceUrl ? 'Open evidence PDF' : 'Generate evidence PDF'}
              </button>
            )}
            {order.payment_method === 'khata' && (
              <button
                data-testid="dispute-khata-btn"
                onClick={async () => {
                  const reason = window.prompt("What's wrong with this bill?\n(e.g., 'Amount is higher than what I bought', 'I never placed this order')");
                  if (!reason || reason.trim().length < 5) return;
                  try {
                    // Find the ledger row for this order
                    const detail = await import('../lib/api').then(m => m.digitalKhata.myDisputes());
                    // Simpler: hit dispute directly using order-based lookup — we use ledger_id from receipt when we saved it.
                    // Fallback: fetch owner khata customer detail via public route not available; use ledger_id passed via url ?dispute=
                    const params = new URLSearchParams(window.location.search);
                    const lid = params.get('dispute');
                    if (!lid) { alert('Please use the dispute link sent on WhatsApp to file a dispute.'); return; }
                    const { digitalKhata } = await import('../lib/api');
                    await digitalKhata.disputeBill(lid, reason);
                    alert('✅ Dispute filed. BharatCart admin will review within 24 hours.');
                  } catch (e) {
                    alert(e?.response?.data?.detail || 'Could not file dispute');
                  }
                }}
                className="no-print inline-flex items-center gap-1.5 rounded-full border border-[#BF360C] bg-white text-[#BF360C] hover:bg-[#FBE9E7] px-3 py-1.5 text-xs font-semibold transition"
              >
                🚩 Dispute this bill
              </button>
            )}
            {localStorage.getItem('mandispot_token') && (
              <a
                data-testid="download-bill-pdf-btn"
                href={`${API_BASE}/orders/${order.id}/bill.pdf?token=${encodeURIComponent(localStorage.getItem('mandispot_token'))}`}
                target="_blank"
                rel="noreferrer"
                className="no-print inline-flex items-center gap-1.5 rounded-full border border-[#0D47A1] bg-[#0D47A1] text-white hover:bg-[#1565C0] hover:border-[#1565C0] px-3 py-1.5 text-xs font-semibold transition"
              >
                <FileDown className="w-3.5 h-3.5"/> Download PDF bill
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Promo / Coin discount summary */}
      {(order.promo_discount > 0 || order.coin_discount > 0) && (
        <div className="card-soft p-4 mt-5 grid gap-2 text-sm">
          {order.promo_discount > 0 && (
            <div className="flex items-center gap-2 text-[#1B5E20]"><Sparkles className="w-4 h-4 text-[#E67E22]"/>WELCOME100 applied — saved ₹{order.promo_discount.toFixed(2)} (paid by BharatCart).</div>
          )}
          {order.coin_discount > 0 && (
            <div className="flex items-center gap-2 text-[#1B5E20]"><Coins className="w-4 h-4 text-[#F2A157]"/>{order.coins_redeemed} MandiCoins redeemed — saved ₹{order.coin_discount.toFixed(2)}.</div>
          )}
        </div>
      )}

      {/* Customer-facing OTP card */}
      {isCustomer && order.otp_required && !order.otp_verified && order.status !== 'delivered' && (
        <section data-testid="customer-otp-card" className="card-soft p-5 mt-5 border-2 border-[#1B5E20]">
          <div className="font-heading font-bold text-lg inline-flex items-center gap-2 text-[#1B5E20]"><ShieldCheck className="w-5 h-5"/>Your delivery OTP</div>
          <p className="text-xs text-ink/60 mt-1">Share this code with the delivery person ONLY after you receive and check your goods.</p>
          <div data-testid="delivery-otp-code" className="font-mono font-black text-4xl tracking-[0.4em] text-[#1B5E20] mt-3 text-center bg-[#E8F5E9] rounded-lg py-4">{order.delivery_otp}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a data-testid="send-otp-whatsapp" href={otpWhatsappUrl} target="_blank" rel="noreferrer" className="btn-secondary inline-flex"><MessageCircle className="w-4 h-4"/>Send via WhatsApp</a>
            <button onClick={() => { navigator.clipboard.writeText(order.delivery_otp); toast.success('OTP copied'); }} className="btn-secondary">Copy OTP</button>
          </div>
        </section>
      )}
      {isCustomer && order.otp_verified && (
        <div className="mt-5 rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] text-[#1B5E20] px-4 py-3 text-sm inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4"/>Delivery OTP verified by the seller.</div>
      )}

      {/* Seller-facing OTP verification */}
      {isOwner && order.otp_required && !order.otp_verified && (
        <section data-testid="seller-otp-card" className="card-soft p-5 mt-5 border-2 border-[#E67E22]">
          <div className="font-heading font-bold text-lg inline-flex items-center gap-2 text-[#D35400]"><ShieldCheck className="w-5 h-5"/>OTP required to deliver</div>
          <p className="text-xs text-ink/60 mt-1">Ask the customer for the 6-digit code shown on their order page (also on their WhatsApp/SMS) before handing over.</p>
          <div className="mt-3 flex gap-2">
            <input
              data-testid="seller-otp-input"
              value={otpInput}
              onChange={e => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit OTP"
              className="flex-1 rounded-lg border border-border bg-white px-3 py-2.5 text-lg font-mono tracking-[0.3em] text-center"
            />
            <button data-testid="seller-verify-otp-btn" disabled={busy || otpInput.length !== 6} onClick={verifyOtp} className="btn-primary">Verify</button>
          </div>
        </section>
      )}

      {/* Timeline */}
      <section className="card-soft p-6 mt-5">
        <div className="font-heading font-bold text-lg">Delivery tracking</div>
        <div className="mt-5 space-y-5">
          {flow.map((s, i) => {
            const done = currentIdx >= i && currentIdx >= 0;
            const active = currentIdx === i;
            const tEntry = order.timeline?.find(t => t.status === s || (s === 'packed' && t.status === 'preparing'));
            return (
              <div key={s} data-testid={`timeline-${s}`} className="flex items-center gap-4">
                <div className="relative">
                  <div className={`w-9 h-9 rounded-full grid place-items-center border ${done?'bg-[#2E7D32] border-[#2E7D32] text-white': active?'bg-[#E67E22] border-[#E67E22] text-white':'bg-white border-border text-ink/40'}`}>
                    {done && !active ? <CheckCircle2 className="w-5 h-5"/> : active ? <Clock className="w-4 h-4"/> : <Circle className="w-4 h-4"/>}
                  </div>
                  {i < flow.length - 1 && <div className="absolute left-1/2 top-9 -translate-x-1/2 w-px h-6 bg-border"/>}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-ink">{labels[s]}</div>
                  <div className="text-xs text-ink/60">{tEntry?.at ? new Date(tEntry.at).toLocaleString() : 'Pending'}</div>
                </div>
                {isOwner && i === currentIdx + 1 && (
                  <button data-testid={`advance-${s}`} disabled={busy || (s === 'delivered' && order.otp_required && !order.otp_verified)} onClick={() => advance(s === 'packed' ? 'packed' : s)} className="btn-primary !py-2 !text-xs">
                    Mark {labels[s]}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {isOwner && order.otp_required && !order.otp_verified && currentIdx >= flow.indexOf('out_for_delivery') && (
          <div className="mt-4 rounded-lg bg-[#FFF3E0] border border-[#FFCC80] text-[#9A4500] px-3 py-2 text-xs">
            Verify the delivery OTP above before marking delivered.
          </div>
        )}
      </section>

      {/* Items + address */}
      <section className="grid md:grid-cols-2 gap-5 mt-6">
        <div className="card-soft p-5">
          <div className="font-heading font-bold text-lg">Items</div>
          <div className="mt-3 space-y-2 text-sm">
            {order.items.map(i => (
              <div key={i.product_id} data-testid={`order-item-${i.product_id}`} className="flex justify-between gap-3 items-start py-2 border-b border-dashed border-border last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div>
                    {i.name} × {i.quantity}
                    {i.important && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-[#E8F5E9] text-[#1B5E20] px-1.5 py-0.5 rounded">Important</span>}
                    {i.costly && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-[#FFF8E1] text-[#7A4A00] px-1.5 py-0.5 rounded">Costly</span>}
                  </div>
                  {i.barcode && (
                    <div className="mt-1.5 inline-block bg-white rounded border border-border p-1.5">
                      <img
                        data-testid={`item-barcode-${i.product_id}`}
                        src={`${API_BASE}/barcode/${encodeURIComponent(i.barcode)}.svg`}
                        alt={`Barcode ${i.barcode}`}
                        className="block max-h-12 w-auto"
                        loading="lazy"
                      />
                      <div className="mt-0.5 text-[9px] font-mono text-ink/60 text-center tracking-widest">{i.barcode}</div>
                    </div>
                  )}
                </div>
                <span className="font-semibold tabular-nums">₹{i.line_total.toFixed(2)}</span>
              </div>
            ))}
            <div className="h-px bg-border my-2"/>
            <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span>₹{order.delivery_fee.toFixed(2)}</span></div>
            {order.promo_discount > 0 && <div className="flex justify-between text-[#1B5E20]"><span>WELCOME100</span><span>– ₹{order.promo_discount.toFixed(2)}</span></div>}
            {order.coin_discount > 0 && <div className="flex justify-between text-[#1B5E20]"><span>MandiCoins ({order.coins_redeemed})</span><span>– ₹{order.coin_discount.toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold"><span>Total</span><span>₹{order.total.toFixed(2)}</span></div>
          </div>
        </div>
        <div className="card-soft p-5">
          {order.shipment && (
            <div data-testid="order-shipment" className="mb-4 rounded-xl bg-[#E8F5E9] border border-[#1B5E20]/30 p-3 text-sm">
              <div className="font-bold text-[#1B5E20]">Shipped by the seller</div>
              <div>Courier: <b>{order.shipment.courier}</b> · Tracking no: <b data-testid="order-tracking-no">{order.shipment.tracking_no}</b></div>
              <div className="text-xs text-ink/60">Dispatched {new Date(order.shipment.dispatched_at).toLocaleString()}</div>
              {order.shipment.tracking_url && <a href={order.shipment.tracking_url} target="_blank" rel="noreferrer" data-testid="order-tracking-link" className="underline text-[#1B5E20] font-semibold">Track your parcel</a>}
            </div>
          )}
          <div className="font-heading font-bold text-lg">Deliver to</div>
          <div className="mt-3 text-sm leading-relaxed">
            <div className="font-semibold">{order.address.full_name}</div>
            <div className="text-ink/70">+91 {order.address.phone}</div>
            <div className="text-ink/70 mt-1">{order.address.line1}, {order.address.village}</div>
            {order.address.landmark && <div className="text-ink/60">Landmark: {order.address.landmark}</div>}
          </div>
        </div>
      </section>

      {/* Post-delivery: scratch card + return */}
      {isCustomer && order.status === 'delivered' && (
        <section className="card-soft p-5 mt-6">
          <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><Coins className="w-5 h-5 text-[#F2A157]"/>Rewards & returns</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-gradient-to-br from-[#FFF4E6] to-[#FFE0B2] border border-[#F2A157] p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8E3B00]">Scratch card</div>
              {scratch ? (
                <div className="mt-2">
                  <div className="font-heading font-black text-3xl text-[#8E3B00]">{scratch.coins} 🪙</div>
                  <div className="text-xs text-ink/70 mt-1">{scratch.tier_label}</div>
                </div>
              ) : (
                <button data-testid="scratch-card-btn" onClick={revealScratch} className="btn-primary mt-3">Scratch to reveal</button>
              )}
            </div>
            <div className="rounded-xl bg-white border border-border p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/60">Need to return?</div>
              {order.return_request ? (
                <div className="mt-2 text-sm">
                  <div className="font-semibold capitalize">Status: {order.return_request.status}</div>
                  <div className="text-xs text-ink/60 mt-1">Submitted {new Date(order.return_request.submitted_at).toLocaleString()}</div>
                </div>
              ) : (
                <>
                  {!retOpen ? (
                    <button data-testid="open-return-btn" onClick={() => setRetOpen(true)} className="btn-secondary mt-3 inline-flex">Request return</button>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <textarea data-testid="return-reason" placeholder="Reason for return" value={retReason} onChange={e=>setRetReason(e.target.value)} className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm" rows={2}/>
                      {order.video_return_required && (
                        <div>
                          <div className="text-[11px] text-[#9A4500] inline-flex items-center gap-1 mb-1"><Video className="w-3 h-3"/>Video proof URL required (upload to Drive/YouTube unlisted)</div>
                          <input data-testid="return-video" placeholder="https://…" value={retVideo} onChange={e=>setRetVideo(e.target.value)} className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <button data-testid="submit-return-btn" disabled={busy} onClick={submitReturn} className="btn-primary !py-2 !text-xs">Submit</button>
                        <button onClick={() => setRetOpen(false)} className="btn-secondary !py-2 !text-xs">Cancel</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Seller view of return request */}
      {isOwner && order.return_request && (
        <section className="card-soft p-5 mt-6 border border-[#FFCC80]">
          <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><Video className="w-5 h-5 text-[#7A4A00]"/>Customer return request</div>
          <div className="mt-2 text-sm">
            <div><span className="text-ink/60">Reason:</span> {order.return_request.reason}</div>
            {order.return_request.video_url && (
              <div className="mt-1"><a href={order.return_request.video_url} target="_blank" rel="noreferrer" className="text-[#D35400] underline">Open video proof</a></div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
